import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerCoach } from '../../api/auth';
import { Card } from '../design-system/Card';
import { Button } from '../design-system/Button';
import { Input } from '../design-system/Input';
import { Select } from '../design-system/Select';

const CoachRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    coachType: 'FITNESS',
  });
  
  const coachTypeOptions = [
    { value: 'FITNESS', label: 'Fitness Coach' },
    { value: 'NUTRITION', label: 'Nutrition Coach' },
    { value: 'WELLNESS', label: 'Mental Wellness Coach' },
    { value: 'LIFE', label: 'Life Coach' },
    { value: 'STRENGTH', label: 'Strength & Conditioning Coach' },
    { value: 'YOGA', label: 'Yoga Instructor' },
    { value: 'PERSONAL_TRAINER', label: 'Personal Trainer' },
    { value: 'DIETITIAN', label: 'Registered Dietitian' },
    { value: 'PHYSICAL_THERAPY', label: 'Physical Therapy Specialist' },
    { value: 'SPORTS_PERFORMANCE', label: 'Sports Performance Coach' },
  ];
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.username) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.coachType) newErrors.coachType = 'Coach type is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await registerCoach({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        first_name: formData.firstName,
        last_name: formData.lastName,
        coach_type: formData.coachType,
      });
      
      // Store tokens
      localStorage.setItem('access_token', response.tokens.access);
      localStorage.setItem('refresh_token', response.tokens.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Navigate to coach onboarding flow
      navigate('/onboarding/coach');
    } catch (error) {
      console.error('Coach registration error:', error);
      
      // Handle axios error response
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error ||
                          error.message || 
                          'Registration failed';
      
      setErrors({
        general: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <Card className="animate-slideUp">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Create Coach Account
            </h2>
            <p className="text-gray-600 mt-2">
              Join our community of wellness professionals
            </p>
          </div>

          {errors.general && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={errors.firstName}
                placeholder="John"
              />
              <Input
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={errors.lastName}
                placeholder="Doe"
              />
            </div>

            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="coach@example.com"
              hint="Your professional email"
            />

            <Input
              label="Username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={errors.username}
              placeholder="coachpro123"
              hint="Your unique identifier"
            />

            <Select
              label="Coach Type"
              name="coachType"
              value={formData.coachType}
              onChange={(e) => handleChange({ target: { name: 'coachType', value: e.target.value } })}
              error={errors.coachType}
              options={coachTypeOptions}
              hint="Select your primary coaching specialty"
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="••••••••"
              hint="At least 8 characters"
            />

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              placeholder="••••••••"
            />

            <div className="space-y-4">
              <Button
                type="submit"
                disabled={loading}
                isLoading={loading}
                variant="primary"
                size="lg"
                fullWidth
                haptic
                celebrateOnClick={!loading}
              >
                {loading ? 'Creating Account...' : 'Create Coach Account'}
              </Button>
              
              <p className="text-sm text-gray-600 text-center">
                You'll complete your profile details in the next step
              </p>
            </div>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/register"
              className="text-sm text-gray-600 hover:text-gray-500"
            >
              ← Back to account type selection
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CoachRegistration;